/* eslint-disable no-new */
import _ from 'underscore';
import Promise from 'bluebird';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { AdCustomizerController } from './controller';
import { AdCustomizerKeywordAnalyzer } from './keyword/analyzer';
import { AdCustomizerIffAnalyzer } from './iff/analyzer';
import { AdCustomizerCountdownAnalyzer } from './countdown/analyzer';
import { AdCustomizerLocationAnalyzer } from './location/analyzer';
import { RSAAdCustomizerAnalyzer } from './rsa-ad-customizer/analyzer';
import { useAdCustomizers } from './hooks';
import { displayLength } from './string-utils';
import { LegacyStringMap } from './rsa-ad-customizer/legacy-string-map';

// These singletons orchestrate the controllers and analyzers for each ad type
// that supports Ad Customizers. This is needed to allow certain ad types to use a subset of the
// available Ad Customizers (specifically the analyzers).
const instances = {
  ResponsiveSearchAd: {
    controller: null,
    initialized: false,
  },
  ResponsiveAd: {
    controller: null,
    initialized: false,
  },
  DynamicSearchAd: {
    controller: null,
    initialized: false,
  },
};

const AdCustomizerAvailableFields = {
  ResponsiveSearchAd: {
    countdown: ['Headlines', 'Descriptions'],
    location: ['Headlines', 'Descriptions', 'Path1', 'Path2'],
    keyword: ['Headlines', 'Descriptions', 'Path1', 'Path2'],
    'rsa-adcustomizer': ['Headlines', 'Descriptions', 'Path1', 'Path2'],
    iff: ['Headlines', 'Descriptions', 'Path1', 'Path2'],
  },
  ResponsiveAd: {
    countdown: ['Headlines', 'Descriptions', 'LongHeadlines'],
    location: ['Headlines', 'Descriptions', 'LongHeadlines'],
    keyword: ['Headlines', 'Descriptions', 'LongHeadlines'],
    'rsa-adcustomizer': ['Headlines', 'Descriptions', 'LongHeadlines'], // pilot?
  },
  DynamicSearchAd: {
    countdown: ['Descriptions', 'Path1', 'Path2'],
    iff: ['Descriptions', 'Path1', 'Path2'],
    keyword: [],
    location: [],
    'rsa-adcustomizer': [],
  },
};

function checkAdType(adType) {
  if (!_.has(instances, adType)) {
    throw new Error(`Ad customizer for ad type '${adType}' may not be supported.`);
  }
}

function initializeAdCustomizer({
  deps,
  adType,
}) {
  checkAdType(adType);

  if (!instances[adType].initialized) {
    instances[adType].controller = new AdCustomizerController(deps);
    const { permissions = {}, showAdCustomizers } = deps;
    const {
      IsRSAAdCustomizerEnabled, IsMMAAdCustomizerInsertionEnabled,
    } = permissions;
    const dynamic = permissions && permissions.dynamic;
    const IsIffPopupEnabled = adType === 'DynamicSearchAd' || (adType === 'ResponsiveSearchAd' && dynamic && dynamic.IsRSAIffPopupEnabled);

    const { controller } = instances[adType];
    if (showAdCustomizers) {
      new AdCustomizerCountdownAnalyzer({
        deps,
        controller,
      });
      new AdCustomizerKeywordAnalyzer({ deps, controller });
      new AdCustomizerLocationAnalyzer({ deps, controller });
      if ((adType === 'ResponsiveAd' && IsMMAAdCustomizerInsertionEnabled) || (adType === 'ResponsiveSearchAd' && IsRSAAdCustomizerEnabled)) {
        new RSAAdCustomizerAnalyzer({
          deps: _.defaults({ i18n: buildLegacyI18n(deps.i18n, [LegacyStringMap]) }, deps),
          controller,
        });
      }
      if (IsIffPopupEnabled) {
        new AdCustomizerIffAnalyzer({ deps, controller });
      }
    }
    instances[adType].initialized = true;
  } else if (deps.scope) {
    // update scope if already initialized
    const { controller: { analyzers } } = instances[adType];
    _.each(analyzers, ({ analyzer }) => {
      // eslint-disable-next-line no-param-reassign
      analyzer.scope = deps.scope;
    });
  }
}

function getController(adType) {
  if (instances[adType].initialized === false || instances[adType].controller === null) {
    throw new Error(`Ad customizer for ad type '${adType}' not be initialized.`);
  }

  return instances[adType].controller;
}

function getAvailableAnalyzers(controller, adType, field) {
  return controller.analyzers.filter(analyzer =>
    AdCustomizerAvailableFields[adType][analyzer.analyzer.name].includes(field));
}

function testResetState(adType) {
  instances[adType].initialized = false;
  instances[adType].controller = null;
}

/*
 * Attaches focus, input event listeners to ad text fields
 */
function attachAdCustomizersHelpers({
  container = document.body, // to attach popups to
  element,
  updateAction,
  setCursorPostion,
  adType,
  field = '',
}) {
  const controller = getController(adType);
  const availableAnalyzers = getAvailableAnalyzers(controller, adType, field);

  const textFieldEventHandler = (event) => {
    const { type, key, target } = event;
    const {
      isSelectedByUser, analyzerName, selectionStart, selectionEnd,
    } = target;
    target.isSelectedByUser = false;
    target.analyzerName = undefined;

    // Ignore if any text is selected, we are only interested in the cursor
    if (selectionStart !== selectionEnd) {
      return;
    }

    // Ignore escape key up since it shouldn't prompt any helper
    if (type === 'keyup' && key === 'Escape') {
      return;
    }

    controller.onInputUpdate({
      availableAnalyzers,
      event,
      isSelectedByUser,
      analyzerName,
      updateAction,
      setCursorPostion,
      container, // to attach popups to
      adType,
      field,
    });
  };

  if (availableAnalyzers.length !== 0) {
    element.addEventListener('click', textFieldEventHandler, false);
    element.addEventListener('keyup', textFieldEventHandler, false);
    element.addEventListener('change', textFieldEventHandler, false);
  }

  return () => {
    element.removeEventListener('click', textFieldEventHandler);
    element.removeEventListener('keyup', textFieldEventHandler);
    element.removeEventListener('change', textFieldEventHandler);
  };
}

// Calculates how much space ad customizer adds to the ad text field
function getCharCountExcludingAdCustomizers({
  text,
  adType,
  field = '',
}) {
  const controller = getController(adType);
  const availableAnalyzers = getAvailableAnalyzers(controller, adType, field);
  if (availableAnalyzers.length === 0) {
    return displayLength(text);
  }

  return controller.getCharCountExcludingAdCustomizers(availableAnalyzers, text, adType, field);
}

/**
 * Looks for ad customizers in given text and substitutes them with their evaluated values.
 * Used for preview generation.
 * @param {string} text - input text
 * @returns {Object} A promise that resolves to modified input text that contains substitutions
 */
function evaluateAdCustomizers({
  text,
  adType,
  field = '',
}) {
  const controller = getController(adType);
  const availableAnalyzers = getAvailableAnalyzers(controller, adType, field);
  if (availableAnalyzers.length === 0) {
    return Promise.resolve(text);
  }

  return controller.evaluateAdCustomizers(availableAnalyzers, text, adType, field);
}

export {
  initializeAdCustomizer,
  evaluateAdCustomizers,
  getCharCountExcludingAdCustomizers,
  attachAdCustomizersHelpers,
  useAdCustomizers,
  testResetState,
  checkAdType,
};
